import { useQuery } from '@tanstack/react-query';
import { FormField, Select } from '../../components/ui/FormField.js';
import type { RoleOption } from '../../hooks/useRoleOptions.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';

interface SuppliersResponse {
  items: { _id: string; company_name: string }[];
}

export const SUPPLIER_REQUIRED_MESSAGE = 'Choose the supplier this role is for.';

/** The suppliers an organisation role can be given for. */
export function useSupplierChoices(enabled: boolean) {
  const { data } = useQuery({
    queryKey: RESOURCE_KEYS.supplier.list,
    queryFn: () => api.get<SuppliersResponse>('/suppliers'),
    enabled,
  });
  return data?.items ?? [];
}

/**
 * 367 (FR-005, design 1c): an organisation role is given within one supplier, so choosing one shows this required
 * field; saving makes the person that supplier's staff with the role.
 */
export function RoleSupplierField({
  id,
  roleName,
  value,
  onChange,
  suppliers,
}: {
  id: string;
  roleName: string;
  value: string;
  onChange: (supplierId: string) => void;
  suppliers: SuppliersResponse['items'];
}) {
  return (
    <FormField
      label="Supplier"
      name={id}
      required
      hint={`${roleName} is given within one supplier.`}
    >
      <Select id={id} name={id} value={value} onChange={(e) => onChange(e.currentTarget.value)}>
        <option value="">Choose a supplier</option>
        {suppliers.map((s) => (
          <option key={s._id} value={s._id}>
            {s.company_name}
          </option>
        ))}
      </Select>
    </FormField>
  );
}

/**
 * 367 (design 1c, 1e): one line on what saving this role means. SuperAdmin is a reserved role name (it cannot be
 * created or renamed on the Roles page), so naming it here is display copy, not a decision: the platform decides
 * who may give it.
 */
export function roleEffectLine(input: {
  personName: string;
  role: RoleOption | undefined;
  supplierName: string | null;
}): string | null {
  if (!input.role) return null;
  if (input.role.name === 'SuperAdmin') {
    return 'SuperAdmin can do everything, including giving SuperAdmin. Recorded in the history.';
  }
  if (input.role.kind === 'organisation' && input.supplierName) {
    return `${input.personName} becomes ${input.supplierName}’s ${input.role.name}.`;
  }
  return null;
}
